// 확정 라인 → 거래명세서 표시 문서. 정본 프로토타입
// docs/invoice-prd/C410_거래명세서발행_세금계산서생성_v2.html 의 buildInvoiceDoc(1263~1400)을 옮긴 것이다.
//
// 금액은 확정값을 집계만 한다. 유형 6의 평균단가는 합계에서 역산한 표시 파생값이다.

import type {
  BuyerInfo,
  InvoiceDocument,
  InvoiceDocumentLine,
  SupplierInfo,
} from '@/lib/api/data-contracts';
import type {
  InvoiceDocumentDto,
  InvoiceDocumentGroup,
  InvoiceDocumentRow,
  InvoiceParty,
} from './invoice-document-types';

/** GET /common의 유종마스터 행. oilKind 1이 유류다(oil.oil_kind) */
export interface OilMaster {
  oilCode?: string;
  oilKind?: string;
  oilName?: string;
}

const OIL_KIND_FUEL = '1';
const FUEL_SPEC = 'L(리터)';
const ETC_SPEC = 'EA';

type GroupMode = 'car-price' | 'price' | 'date' | 'car-date' | 'car-avg';

interface PrintOption {
  mode: GroupMode;
  oilSub: boolean;
  carSub: boolean;
  grandTot: boolean;
  perItemPage: boolean;
}

/** 인쇄유형별 렌더 규칙. 정본 buildInvoiceDoc의 OPT 표와 같다 */
const OPT: Record<string, PrintOption> = {
  // 유종별·차량별·단가별 집계
  '1': { mode: 'car-price', oilSub: true, carSub: false, grandTot: true, perItemPage: false },
  // 유종별·단가별 집계
  '2': { mode: 'price', oilSub: false, carSub: false, grandTot: true, perItemPage: false },
  // 유종별·일자별·단가별 집계
  '3': { mode: 'date', oilSub: true, carSub: false, grandTot: true, perItemPage: false },
  // 유종(1장씩)·차량별·단가별 집계
  '4': { mode: 'car-price', oilSub: true, carSub: false, grandTot: false, perItemPage: true },
  // 유종별·차량별·일자별 리스트
  '5': { mode: 'car-date', oilSub: true, carSub: true, grandTot: true, perItemPage: false },
  // 유종·차량별 집계(평균단가)
  '6': { mode: 'car-avg', oilSub: true, carSub: false, grandTot: true, perItemPage: false },
  // 유종(1장씩)·차량·일자별 리스트
  '7': { mode: 'car-date', oilSub: true, carSub: true, grandTot: false, perItemPage: true },
};

const DEFAULT_PRINT_TYPE = '5';

function groupBy<T>(rows: T[], key: (row: T) => string): Map<string, T[]> {
  const groups = new Map<string, T[]>();
  for (const row of rows) {
    const k = key(row);
    const bucket = groups.get(k);
    if (bucket) {
      bucket.push(row);
    } else {
      groups.set(k, [row]);
    }
  }
  return groups;
}

/**
 * 10자리 사업자번호를 3-2-5 하이픈 표기로 바꾼다.
 * web.company.biz_no가 varchar(11)이라 하이픈 없이 저장되는데(거래처 ent_code는 하이픈 포함),
 * 인쇄 표기는 통일한다. 이미 하이픈이 있거나 형식이 다르면 그대로 둔다.
 */
export function formatBizNo(value: string) {
  return /^\d{10}$/.test(value) ? `${value.slice(0, 3)}-${value.slice(3, 5)}-${value.slice(5)}` : value;
}

/** 차량NO 오름차순 그룹. 서버 라인은 판매일 순이라 그대로 그리면 차량이 뒤섞인다 */
function carGroupsSorted(rows: InvoiceDocumentLine[]) {
  return [...groupBy(rows, r => r.carNo ?? '').entries()].sort(([a], [b]) => a.localeCompare(b));
}

function sumRows(rows: InvoiceDocumentLine[]) {
  return {
    qty: rows.reduce((s, r) => s + (r.qty ?? 0), 0),
    sup: rows.reduce((s, r) => s + (r.supplyAmt ?? 0), 0),
    vat: rows.reduce((s, r) => s + (r.vatAmt ?? 0), 0),
  };
}

/** 실제 양식은 수량 소수 2자리, 단가 정수로 표기한다(원자료의 부동소수 오차 노출 방지) */
function roundQty(qty: number) {
  return Math.round(qty * 100) / 100;
}

function roundPrice(price: number | undefined) {
  return price == null ? null : Math.round(price);
}

/** '2026-07-31' → '07-31' */
function monthDay(sdate: string | undefined) {
  return sdate ? sdate.slice(5) : '';
}

function detailRow(
  dateText: string,
  itemName: string,
  unit: string,
  carNo: string,
  qty: number,
  unitPrice: number | null,
  supplyAmt: number,
  vatAmt: number
): InvoiceDocumentRow {
  return {
    rowType: 'detail',
    dateText,
    itemName,
    unit,
    carNo,
    totalAmt: supplyAmt + vatAmt,
    qty,
    unitPrice,
    supplyAmt,
    vatAmt,
  };
}

function subtotalRow(
  rowType: Exclude<InvoiceDocumentRow['rowType'], 'detail'>,
  rows: InvoiceDocumentLine[]
): InvoiceDocumentRow {
  const s = sumRows(rows);
  return {
    rowType,
    dateText: '',
    itemName: '',
    unit: '',
    carNo: '',
    totalAmt: s.sup + s.vat,
    qty: roundQty(s.qty),
    unitPrice: null,
    supplyAmt: s.sup,
    vatAmt: s.vat,
  };
}

export function buildInvoiceDocument(
  document: InvoiceDocument,
  supplier: SupplierInfo | undefined,
  oils: OilMaster[]
): InvoiceDocumentDto {
  const oilByCode = new Map(oils.filter(o => o.oilCode).map(o => [o.oilCode as string, o]));
  const isFuel = (oilType: string) => oilByCode.get(oilType)?.oilKind === OIL_KIND_FUEL;
  const itemLabel = (oilType: string) => {
    const name = oilByCode.get(oilType)?.oilName ?? oilType;
    return isFuel(oilType) ? `${name}-ℓ` : name;
  };
  const unitLabel = (oilType: string) => (isFuel(oilType) ? FUEL_SPEC : ETC_SPEC);

  const printType = document.printType ?? DEFAULT_PRINT_TYPE;
  const opt = OPT[printType] ?? OPT[DEFAULT_PRINT_TYPE];
  const lines = document.lines ?? [];
  const byOil = groupBy(lines, line => line.oilType ?? '');

  function buildOilRows(oilType: string, rows: InvoiceDocumentLine[]): InvoiceDocumentRow[] {
    const item = itemLabel(oilType);
    const unit = unitLabel(oilType);
    const out: InvoiceDocumentRow[] = [];

    if (opt.mode === 'car-price') {
      for (const [carNo, carRows] of carGroupsSorted(rows)) {
        for (const priceRows of groupBy(carRows, r => String(r.unitPrice ?? '')).values()) {
          const s = sumRows(priceRows);
          out.push(detailRow('', item, unit, carNo, roundQty(s.qty),
            roundPrice(priceRows[0].unitPrice), s.sup, s.vat));
        }
      }
    } else if (opt.mode === 'price') {
      for (const priceRows of groupBy(rows, r => String(r.unitPrice ?? '')).values()) {
        const s = sumRows(priceRows);
        const lastDate = priceRows.reduce(
          (max, r) => ((r.sdate ?? '') > max ? (r.sdate as string) : max),
          priceRows[0].sdate ?? ''
        );
        out.push(detailRow(monthDay(lastDate), item, unit, '', roundQty(s.qty),
          roundPrice(priceRows[0].unitPrice), s.sup, s.vat));
      }
    } else if (opt.mode === 'date') {
      const byDate = groupBy(rows, r => r.sdate ?? '');
      for (const sdate of [...byDate.keys()].sort()) {
        const dateRows = byDate.get(sdate) as InvoiceDocumentLine[];
        const s = sumRows(dateRows);
        out.push(detailRow(monthDay(sdate), item, unit, '', roundQty(s.qty),
          roundPrice(dateRows[0].unitPrice), s.sup, s.vat));
      }
    } else if (opt.mode === 'car-date') {
      for (const [carNo, carRows] of carGroupsSorted(rows)) {
        const sorted = [...carRows].sort((a, b) => (a.sdate ?? '').localeCompare(b.sdate ?? ''));
        for (const line of sorted) {
          out.push(detailRow(monthDay(line.sdate), item, unit, carNo, roundQty(line.qty ?? 0),
            roundPrice(line.unitPrice), line.supplyAmt ?? 0, line.vatAmt ?? 0));
        }
        if (opt.carSub) {
          out.push(subtotalRow('carSubtotal', sorted));
        }
      }
    } else {
      // car-avg: 평균단가는 합계에서 역산한 표시 파생값이다
      for (const [carNo, carRows] of carGroupsSorted(rows)) {
        const s = sumRows(carRows);
        const avgPrice = s.qty ? Math.round((s.sup * 1.1) / s.qty) : 0;
        out.push(detailRow('', item, unit, carNo, Math.round(s.qty), avgPrice, s.sup, s.vat));
      }
    }
    return out;
  }

  const oilBlocks = [...byOil.entries()].map(([oilType, rows]) => {
    const body = buildOilRows(oilType, rows);
    if (opt.oilSub) {
      body.push(subtotalRow('oilSubtotal', rows));
    }
    return { oilType, rows, body };
  });

  let groups: InvoiceDocumentGroup[];
  if (opt.perItemPage) {
    // 유형 4·7: 유종 1장씩 별지
    groups = oilBlocks.map(block => ({
      key: block.oilType,
      tableTitle:
        printType === '7' ? `거 래 내 역 ( ${itemLabel(block.oilType)} )` : '거 래 내 역',
      rows: block.body,
    }));
  } else {
    const rows = oilBlocks.flatMap(block => block.body);
    if (opt.grandTot) {
      rows.push(subtotalRow('grandTotal', lines));
    }
    groups = [{ key: '', tableTitle: '거 래 내 역', rows }];
  }

  return {
    docNo: document.docNo ?? '',
    geoEcode: document.geoEcode ?? '',
    header: {
      issueYm: document.issueYm ?? '',
      titleText: titleText(document.issueYm),
      printType: Number(printType) as InvoiceDocumentDto['header']['printType'],
      supplier: toSupplierParty(supplier),
      customer: toBuyerParty(document.buyer),
    },
    groups,
  };
}

/** '202607' → '2026년 07월분 거래명세서' */
function titleText(issueYm: string | undefined) {
  if (!issueYm || issueYm.length < 6) {
    return '거래명세서';
  }
  return `${issueYm.slice(0, 4)}년 ${issueYm.slice(4, 6)}월분 거래명세서`;
}

function toSupplierParty(supplier: SupplierInfo | undefined): InvoiceParty {
  return {
    bizNo: formatBizNo(supplier?.entCode ?? ''),
    name: supplier?.name ?? '',
    ceo: supplier?.rep ?? '',
    address: supplier?.addr ?? '',
  };
}

/** 미등록문서·마스터 삭제 등으로 공급받는자가 없을 수 있다 */
function toBuyerParty(buyer: BuyerInfo | undefined): InvoiceParty {
  return {
    bizNo: formatBizNo(buyer?.entCode ?? ''),
    name: buyer?.name ?? '',
    ceo: buyer?.rep ?? '',
    address: buyer?.addr ?? '',
    code: buyer?.dealCode,
  };
}
